import { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  SquareUserRound,
  TrendingDown,
  TrendingUp,
  UserRoundPlus,
} from "lucide-react";
import { DefinitionTooltip } from "src/components/common";
import { getFacebookFollowersDefinitionInfo } from "../OrganicMediaDefinitions";
import { formatNumber } from "src/utils/numberFormat";
import "../../../styles/FacebookFollowersReport/FacebookFollowersKpiCards.scss";

export type FacebookFollowersKpiCard = {
  title: string;
  value: string;
  percentageChange?: number;
  comparisonLabel?: string;
  tooltipLabel?: string;
};

type FacebookFollowersKpiCardsProps = {
  kpis: FacebookFollowersKpiCard[];
};

const formatComparisonLabel = (label: string | undefined, t: (_key: string) => string) => {
  const trimmed = (label ?? "").trim();
  const normalized = trimmed.toLowerCase();
  if (
    !normalized ||
    normalized === "vs last period" ||
    normalized === "vs previous period" ||
    trimmed === "مقارنة بالفترة السابقة"
  ) {
    return t("organicMedia.tooltips.vsPreviousPeriod").replace(/:$/, "");
  }
  return label as string;
};

const formatKpiCardValue = (title: string, value: string, language: string) => {
  const normalizedTitle = title.trim().toLowerCase();
  if (normalizedTitle.includes("avg") && normalizedTitle.includes("new followers")) {
    const numeric = Number(value.replace(/,/g, ""));
    if (Number.isFinite(numeric)) {
      return formatNumber(Math.round(numeric), {}, language);
    }
  }
  return value;
};

const FacebookFollowersKpiCards: FC<FacebookFollowersKpiCardsProps> = ({ kpis }) => {
  const { t, i18n } = useTranslation();

  return (
    <div className="organic-facebook-followers-layout__kpis">
      {kpis.slice(0, 2).map((card, index) => {
        const isTotalFollowers = card.title.trim().toLowerCase() === "total followers";
        const comparisonPercent =
          typeof card.percentageChange === "number" && Number.isFinite(card.percentageChange)
            ? card.percentageChange
            : null;
        const hasComparison = !isTotalFollowers && comparisonPercent !== null;
        const comparisonTone =
          comparisonPercent === null
            ? "neutral"
            : comparisonPercent > 0
              ? "positive"
              : comparisonPercent < 0
                ? "negative"
                : "neutral";
        return (
          <div
            key={card.title}
            className="organic-followers-kpi organic-followers-kpi--facebook-modern"
          >
            <div className="organic-followers-kpi__badge" aria-hidden>
              {index === 0 ? (
                <SquareUserRound size={18} />
              ) : (
                <UserRoundPlus size={18} />
              )}
            </div>
            <div className="organic-content-panel__title">
              <h3>{card.title}</h3>
              <DefinitionTooltip
                label={card.title}
                info={getFacebookFollowersDefinitionInfo(
                  card.title,
                  card.tooltipLabel,
                  t,
                )}
                align={index >= 2 ? "end" : "start"}
                formulaTitle={t("organicMedia.common.formula")}
              />
            </div>
            <div className="organic-followers-kpi__content">
              <div className="organic-followers-kpi__meta">
                <div className="organic-followers-kpi__value">
                  {formatKpiCardValue(card.title, card.value, i18n.language)}
                </div>
                {!isTotalFollowers && hasComparison && comparisonPercent !== null ? (
                  <div className="organic-followers-kpi__period">
                    <span
                      className={`organic-followers-kpi__change organic-followers-kpi__change--${comparisonTone}`}
                    >
                      {comparisonPercent > 0 ? (
                        <TrendingUp size={12} aria-hidden />
                      ) : comparisonPercent < 0 ? (
                        <TrendingDown size={12} aria-hidden />
                      ) : null}
                      {`${formatNumber(comparisonPercent, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, i18n.language)}%`}
                    </span>{" "}
                    {formatComparisonLabel(card.comparisonLabel, t)}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FacebookFollowersKpiCards;
